import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  TablePagination,
  InputAdornment,
  Switch,
  FormControlLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  JOB_STATUSES, JOB_STATUS_COLORS,
  EMPLOYMENT_TYPES, PRIORITY_OPTIONS,
  formatEnumLabel,
} from '../../../../constants/hrm';
import {
  useJobs, useCreateJob, useUpdateJob, useDeleteJob,
  useDepartments,
} from '../../../../hooks/useHRM';
import { hasPermission } from '../../../../utils/permissionUtils';

const initialFormData = {
  title: '',
  description: '',
  requirements: '',
  department_id: '',
  position_id: '',
  location: '',
  is_remote: false,
  employment_type: 'FULL_TIME',
  salary_min: '',
  salary_max: '',
  vacancies: 1,
  priority: 'MEDIUM',
  closing_date: '',
  is_internal: true,
  is_external: true,
};

const JobList = () => {
  const { chatbotId } = useParams();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingJob, setDeletingJob] = useState(null);
  const [error, setError] = useState(null);

  const [canView, setCanView] = useState(false);
  const [canModify, setCanModify] = useState(false);

  useEffect(() => {
    const checkPermissions = async () => {
      const view = await hasPermission('hrm', 'view');
      const modify = await hasPermission('hrm', 'add|edit');
      setCanView(view);
      setCanModify(modify);
    };
    checkPermissions();
  }, []);

  const filters = {
    page: page + 1,
    per_page: rowsPerPage,
    ...(search && { search }),
    ...(statusFilter && { status: statusFilter }),
    ...(departmentFilter && { department_id: departmentFilter }),
  };

  const { data: jobsData, isLoading: loading, refetch: refetchJobs } = useJobs(chatbotId, filters, { enabled: !!chatbotId && canView });
  const { data: departmentsData } = useDepartments(chatbotId, { per_page: 100 }, { enabled: !!chatbotId && canView });

  const jobs = jobsData?.data || [];
  const totalCount = jobsData?.pagination?.total || 0;
  const departments = departmentsData?.data || [];

  const createJob = useCreateJob(chatbotId);
  const updateJob = useUpdateJob(chatbotId);
  const deleteJob = useDeleteJob(chatbotId);

  const saving = createJob.isPending || updateJob.isPending;
  const deleting = deleteJob.isPending;

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        title: job.title || '',
        description: job.description || '',
        requirements: job.requirements || '',
        department_id: job.department_id || '',
        position_id: job.position_id || '',
        location: job.location || '',
        is_remote: job.is_remote || false,
        employment_type: job.employment_type || 'FULL_TIME',
        salary_min: job.salary_min || '',
        salary_max: job.salary_max || '',
        vacancies: job.vacancies || 1,
        priority: job.priority || 'MEDIUM',
        closing_date: job.closing_date ? job.closing_date.split('T')[0] : '',
        is_internal: job.is_internal !== undefined ? job.is_internal : true,
        is_external: job.is_external !== undefined ? job.is_external : true,
      });
    } else {
      setEditingJob(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingJob(null);
    setFormData(initialFormData);
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        salary_min: formData.salary_min ? Number(formData.salary_min) : null,
        salary_max: formData.salary_max ? Number(formData.salary_max) : null,
        vacancies: Number(formData.vacancies),
        department_id: formData.department_id || null,
        position_id: formData.position_id || null,
      };

      if (editingJob) {
        await updateJob.mutateAsync({ id: editingJob.id, ...payload });
      } else {
        await createJob.mutateAsync(payload);
      }
      handleCloseDialog();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save job');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJob.mutateAsync(deletingJob.id);
      setDeleteConfirmOpen(false);
      setDeletingJob(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete job');
    }
  };

  const handleRowClick = (job) => {
    navigate(`/chatbot/${chatbotId}/hrm/jobs/${job.id}`);
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have permission to view the HRM module.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Job Requisitions
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Refresh">
            <IconButton onClick={() => refetchJobs()} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {canModify && (
            <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
              Create Job
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search jobs..."
          value={search}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {JOB_STATUSES.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Department</InputLabel>
          <Select
            value={departmentFilter}
            label="Department"
            onChange={(e) => { setDepartmentFilter(e.target.value); setPage(0); }}
          >
            <MenuItem value="">All</MenuItem>
            {departments.map((d) => (
              <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Table sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell align="center">Vacancies</TableCell>
                  <TableCell align="center">Applicants</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Posted Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No jobs found. Create your first job requisition.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobs.map((job) => (
                    <TableRow
                      key={job.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(job)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {job.title}
                        </Typography>
                      </TableCell>
                      <TableCell>{job.department_name || '-'}</TableCell>
                      <TableCell>
                        {job.location || '-'}
                        {job.is_remote && (
                          <Chip label="Remote" size="small" sx={{ ml: 1 }} variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">{job.vacancies || 0}</TableCell>
                      <TableCell align="center">{job.applicant_count || 0}</TableCell>
                      <TableCell>
                        <Chip
                          label={job.status}
                          size="small"
                          color={JOB_STATUS_COLORS[job.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {job.posted_date
                          ? new Date(job.posted_date).toLocaleDateString()
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {canModify && (
                          <>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDialog(job);
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeletingJob(job);
                                  setDeleteConfirmOpen(true);
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
            />
          </>
        )}
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingJob ? 'Edit Job' : 'Create Job'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={4}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Requirements"
                value={formData.requirements}
                onChange={(e) => handleFormChange('requirements', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={formData.department_id}
                  label="Department"
                  onChange={(e) => handleFormChange('department_id', e.target.value)}
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Position ID"
                value={formData.position_id}
                onChange={(e) => handleFormChange('position_id', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_remote}
                    onChange={(e) => handleFormChange('is_remote', e.target.checked)}
                  />
                }
                label="Remote Position"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.employment_type}
                  label="Employment Type"
                  onChange={(e) => handleFormChange('employment_type', e.target.value)}
                >
                  {EMPLOYMENT_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>{formatEnumLabel(t)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Min Salary"
                type="number"
                value={formData.salary_min}
                onChange={(e) => handleFormChange('salary_min', e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Max Salary"
                type="number"
                value={formData.salary_max}
                onChange={(e) => handleFormChange('salary_max', e.target.value)}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                label="Vacancies"
                type="number"
                value={formData.vacancies}
                onChange={(e) => handleFormChange('vacancies', e.target.value)}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Closing Date"
                type="date"
                value={formData.closing_date}
                onChange={(e) => handleFormChange('closing_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_internal}
                    onChange={(e) => handleFormChange('is_internal', e.target.checked)}
                  />
                }
                label="Internal"
              />
            </Grid>
            <Grid item xs={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_external}
                    onChange={(e) => handleFormChange('is_external', e.target.checked)}
                  />
                }
                label="External"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.title}
          >
            {saving ? <CircularProgress size={24} /> : editingJob ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Job</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the job "{deletingJob?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default JobList;
